'use client'

import Link from 'next/link'
import { Facebook, Home, MessageCircle } from 'lucide-react'
import { useLanguage } from '@/contexts/LanguageContext'
import { navigationTranslations } from '@/translations/navigation'

export default function Footer() {
  const currentYear = new Date().getFullYear()
  const { language } = useLanguage()
  const t = navigationTranslations[language]

  const areaNames = language === 'en'
    ? ['Barry', 'Penarth', 'Dinas Powys', 'Cardiff', 'Bridgend']
    : ['Barri', 'Penarth', 'Dinas Powys', 'Caerdydd', 'Pen-y-bont ar Ogwr']

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
          {/* Company Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">JC Gardening</h3>
            <p className="text-sm">
              {t.footer.companyInfo.description}
            </p>
            <div className="flex space-x-4">
              <a
                href="https://www.facebook.com/share/1AbDKvFM2c/?mibextid=wwXIfr"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="h-6 w-6" />
              </a>
              <a
                href="https://nextdoor.co.uk/pages/jc-gardening-2/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Nextdoor"
              >
                <Home className="h-6 w-6" />
              </a>
              <a
                href="https://wa.me/447879643396?text=Hi! I'm interested in your garden maintenance services. Could you please provide a quote?"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-white transition-colors"
                aria-label={language === 'en' ? 'Contact us on WhatsApp' : 'Cysylltwch â ni ar WhatsApp'}
              >
                <MessageCircle className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">{t.footer.quickLinks.title}</h3>
            <ul className="space-y-2">
              <li>
                <Link href="#services" className="text-gray-300 hover:text-white transition-colors">
                  {t.footer.quickLinks.services}
                </Link>
              </li>
              <li>
                <Link href="#portfolio" className="text-gray-300 hover:text-white transition-colors">
                  {t.footer.quickLinks.portfolio}
                </Link>
              </li>
              <li>
                <Link href="#testimonials" className="text-gray-300 hover:text-white transition-colors">
                  {t.footer.quickLinks.testimonials}
                </Link>
              </li>
              <li>
                <Link href="#contact" className="text-gray-300 hover:text-white transition-colors">
                  {t.footer.quickLinks.contact}
                </Link>
              </li>
            </ul>
          </div>

          {/* Service Areas */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">{t.footer.serviceAreas.title}</h3>
            <ul className="space-y-2">
              {areaNames.map((name) => (
                <li key={name} className="text-gray-300">{name}</li>
              ))}
              <li className="text-gray-300">{t.footer.serviceAreas.surroundingAreas}</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">{t.footer.contactInfo.title}</h3>
            <ul className="space-y-2">
              <li>
                <a
                  href="tel:+447879643396"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  (+44) 7879643396
                </a>
              </li>
              <li>
                <a
                  href="mailto:jcgardening21@outlook.com"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  jcgardening21@outlook.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 border-t border-gray-800 pt-8">
          <div className="flex flex-col items-center justify-between space-y-4 sm:flex-row sm:space-y-0">
            <p className="text-sm text-gray-300">
              {t.footer.bottomBar.copyright.replace('{year}', currentYear.toString())}
            </p>
            <div className="flex space-x-6 text-sm">
              <Link href="/privacy" className="text-gray-300 hover:text-white transition-colors">
                {t.footer.bottomBar.privacyPolicy}
              </Link>
              <Link href="/terms" className="text-gray-300 hover:text-white transition-colors">
                {t.footer.bottomBar.termsOfService}
              </Link>
              <Link href="/cookies" className="text-gray-300 hover:text-white transition-colors">
                {t.footer.bottomBar.cookiePolicy}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
} 